import Service, { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import type Owner from '@ember/owner'
import type RouterService from '@ember/routing/router-service'
import type SessionService from 'teacher-ui-client/services/session'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type { PreviewEngine } from 'teacher-ui-client/interactive-preview/engines'

interface PreviewOptions {
  title?: string
  startFullscreen?: boolean
}

/**
 * https://3plearning.atlassian.net/wiki/spaces/TY/pages/3811278862/Interactive+Preview+Engines+in+TUI
 */
export default class InteractivePreviewService extends Service {
  @service session!: SessionService

  @service router!: RouterService

  @service teacherEvents!: TeacherEvents

  @tracked isFullscreen = false

  @tracked selectedPreview: Nullable<PreviewEngine> = null

  @tracked options: PreviewOptions = {}

  constructor(owner: Owner) {
    super(owner)
    /* Ensure the preview closes if the user hits the back button. Otherwise it can be very confusing if the preview
     * stays open fullscreen in the foreground */
    this.router.on('routeDidChange', this.closePreview)
  }

  willDestroy() {
    this.router.off('routeDidChange', this.closePreview)
    super.willDestroy()
  }

  get curriculum() {
    return this.session.currentSchool.accent.extension
  }

  openPreview = (preview: PreviewEngine, options: PreviewOptions = {}) => {
    const { product, eventData } = preview.getAnalyticsMetadata()

    this.options = options
    preview.onComplete = () => this.closePreview()
    this.teacherEvents.sendEvent('interactive-opened', product, eventData)
    this.setFullscreen(Boolean(options.startFullscreen))
    this.setPreview(preview)
  }

  closePreview = () => {
    this.selectedPreview = null
    this.isFullscreen = false
  }

  setPreview = (preview: PreviewEngine) => {
    this.selectedPreview = preview
  }

  setFullscreen = (val: boolean) => {
    this.isFullscreen = val
  }
}

declare module '@ember/service' {
  interface Registry {
    'interactive-preview': InteractivePreviewService
  }
}
